// src/components/league-dashboard/TeamLinkDropdown.js - SIMPLIFIED WITHOUT COMMISSIONER CONTEXT
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Users, BarChart3 } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

const TeamLinkDropdown = React.memo(({ team, leagueId, className = '' }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const rafIdRef = useRef(null);
  const navigate = useNavigate();

  // Stable position updater
  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left
      });
    }
  }, []);

  useEffect(() => {
    if (!showDropdown) return;

    // Update position immediately when dropdown opens
    updatePosition();

    // FIX 3A: Defer outside-click listener by one animation frame so the
    // originating click fully resolves before detection activates.
    let mousedownHandler;
    rafIdRef.current = requestAnimationFrame(() => {
      mousedownHandler = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
            buttonRef.current && !buttonRef.current.contains(event.target)) {
          setShowDropdown(false);
        }
      };
      document.addEventListener('mousedown', mousedownHandler);
    });

    const handleScroll = () => {
      updatePosition();
    };
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (mousedownHandler) {
        document.removeEventListener('mousedown', mousedownHandler);
      }
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showDropdown, updatePosition]);

  const handleTeamClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 2,
        left: rect.left
      });
    }

    setShowDropdown(prev => !prev);
  }, [showDropdown]);

  const handleNavigate = useCallback((section) => {
    setShowDropdown(false);
    navigate(`/leagues/${leagueId}`, {
      state: {
        activeSection: section,
        targetTeamId: team.team_id,
        targetTeamName: team.team_name
      }
    });
  }, [navigate, leagueId, team.team_id, team.team_name]);

  // FIX 3B: Portal renders to document.body, positioned via getBoundingClientRect.
  // z-[99999] ensures it's above any sticky column z-10 (Fix 3D).
  const dropdownContent = showDropdown && ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className={`fixed z-[99999] ${dynastyTheme.classes.bg.darkFlat} border ${dynastyTheme.classes.border.primary} rounded shadow-xl overflow-hidden`}
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        minWidth: '120px'
      }}
    >
      <button
        onClick={() => handleNavigate('my-roster')}
        className={`w-full px-3 py-1.5 flex items-center gap-1.5 hover:bg-yellow-400/20 ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.text.primary} transition-all duration-150 text-left text-xs`}
      >
        <Users className="w-3 h-3" />
        <span>Roster</span>
      </button>

      <button
        onClick={() => handleNavigate('team-stats')}
        className={`w-full px-3 py-1.5 flex items-center gap-1.5 hover:bg-yellow-400/20 ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.text.primary} transition-all duration-150 text-left text-xs border-t ${dynastyTheme.classes.border.neutral}`}
      >
        <BarChart3 className="w-3 h-3" />
        <span>Stats</span>
      </button>
    </div>,
    document.body
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleTeamClick}
        className={`${className} hover:${dynastyTheme.classes.text.primary} transition-colors duration-150 cursor-pointer relative`}
      >
        {team.team_name}
        {showDropdown && (
          <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-yellow-400"></span>
        )}
      </button>
      {dropdownContent}
    </>
  );
});

TeamLinkDropdown.displayName = 'TeamLinkDropdown';

export default TeamLinkDropdown;
